/**
 * Utility functions for the Exhibit Curator app
 */

/**
 * Function to determine period based on date and style
 * @param {number} dateStart - Start date of the artwork
 * @param {number} dateEnd - End date of the artwork  
 * @param {Array} styleTitles - Array of style titles from the API
 * @returns {string} The determined period
 */
export const determinePeriod = (dateStart, dateEnd, styleTitles) => {
  if (styleTitles && styleTitles.length > 0) {
    return styleTitles[0];
  }
  
  const year = dateStart || dateEnd;
  if (!year) return 'Unknown Period';
  
  if (year < 1400) return 'Medieval';
  if (year < 1600) return 'Renaissance';
  if (year < 1750) return 'Baroque';
  if (year < 1850) return 'Neoclassical';
  if (year < 1900) return 'Impressionist';
  if (year < 1945) return 'Modern';
  return 'Contemporary';
};

/**
 * Function to normalize image URLs.
 * - If given a full URL (e.g., from The Met API), return as-is.
 * - If given an AIC image_id, construct the IIIF URL for backward compatibility.
 * @param {string} src - Image URL or image_id
 * @returns {string|null} A usable image URL or null if not provided
 */
export const getImageUrl = (src) => {
  if (!src) return null;
  if (/^https?:\/\//i.test(src)) return src; // Already a URL (e.g., The Met's primaryImageSmall)
  // Fallback for legacy AIC image_id values
  return `https://www.artic.edu/iiif/2/${src}/full/843,/0/default.jpg`;
};

/**
 * Async delay utility function
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise} Promise that resolves after the delay
 */
export const delay = (ms) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Fallback artwork data when API is unavailable
 */
export const fallbackArtworkData = [
  { 
    id: 1, 
    title: "The Great Wave off Kanagawa", 
    artist: "Katsushika Hokusai", 
    year: "1831", 
    period: "Modern", 
    tags: ["PRINTS", "JAPANESE"], 
    image_id: "https://images.metmuseum.org/CRDImages/as/original/DP140985.jpg",
    department: "Asian Art",
    culture: "Japan",
    link: "https://www.metmuseum.org/art/collection/search/45434"
  },
  { 
    id: 2, 
    title: "Washington Crossing the Delaware", 
    artist: "Emanuel Leutze", 
    year: "1851", 
    period: "Contemporary", 
    tags: ["PAINTINGS", "AMERICAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ad/original/DT4973.jpg",
    department: "American Decorative Arts",
    culture: "American",
    link: "https://www.metmuseum.org/art/collection/search/11417"
  },
  { 
    id: 3, 
    title: "Venus and Adonis", 
    artist: "Titian", 
    year: "1560", 
    period: "Renaissance", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1567.jpg",
    department: "European Paintings",
    culture: "Italian",
    link: "https://www.metmuseum.org/art/collection/search/437329"
  },
  { 
    id: 4, 
    title: "The Death of Socrates", 
    artist: "Jacques-Louis David", 
    year: "1787", 
    period: "Neoclassical", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT2207.jpg",
    department: "European Paintings",
    culture: "French",
    link: "https://www.metmuseum.org/art/collection/search/436105"
  },
  { 
    id: 5, 
    title: "Self-Portrait with a Straw Hat", 
    artist: "Vincent van Gogh", 
    year: "1887", 
    period: "Modern", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1502.jpg",
    department: "European Paintings",
    culture: "Dutch",
    link: "https://www.metmuseum.org/art/collection/search/436532"
  },
  { 
    id: 6, 
    title: "The Harvesters", 
    artist: "Pieter Bruegel the Elder", 
    year: "1565", 
    period: "Renaissance", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1877.jpg",
    department: "European Paintings",
    culture: "Flemish",
    link: "https://www.metmuseum.org/art/collection/search/435809"
  },
  { 
    id: 7, 
    title: "Madame X", 
    artist: "John Singer Sargent", 
    year: "1884", 
    period: "Modern", 
    tags: ["PAINTINGS", "AMERICAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ad/original/DT1581.jpg",
    department: "American Wing",
    culture: "American",
    link: "https://www.metmuseum.org/art/collection/search/12127"
  },
  { 
    id: 8, 
    title: "The Temple of Dendur", 
    artist: "Ancient Egyptian", 
    year: "15 BC", 
    period: "Ancient", 
    tags: ["ARCHITECTURE", "EGYPTIAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/eg/original/DT218944.jpg",
    department: "Egyptian Art",
    culture: "Egyptian",
    link: "https://www.metmuseum.org/art/collection/search/547802"
  },
  { 
    id: 9, 
    title: "Garden at Sainte-Adresse", 
    artist: "Claude Monet", 
    year: "1867", 
    period: "Impressionist", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1943.jpg",
    department: "European Paintings",
    culture: "French",
    link: "https://www.metmuseum.org/art/collection/search/437133"
  },
  { 
    id: 10, 
    title: "The Musicians", 
    artist: "Caravaggio", 
    year: "1597", 
    period: "Baroque", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT258994.jpg",
    department: "European Paintings",
    culture: "Italian",
    link: "https://www.metmuseum.org/art/collection/search/436535"
  },
  { 
    id: 11, 
    title: "Wheat Field with Cypresses", 
    artist: "Vincent van Gogh", 
    year: "1889", 
    period: "Modern", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1567.jpg",
    department: "European Paintings",
    culture: "Dutch",
    link: "https://www.metmuseum.org/art/collection/search/436535"
  },
  { 
    id: 12, 
    title: "Young Woman with a Water Pitcher", 
    artist: "Johannes Vermeer", 
    year: "1662", 
    period: "Baroque", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1890.jpg",
    department: "European Paintings",
    culture: "Dutch",
    link: "https://www.metmuseum.org/art/collection/search/437869"
  },
  { 
    id: 13, 
    title: "The Card Players", 
    artist: "Paul Cézanne", 
    year: "1890", 
    period: "Modern", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1956.jpg",
    department: "European Paintings",
    culture: "French",
    link: "https://www.metmuseum.org/art/collection/search/435868"
  },
  { 
    id: 14, 
    title: "Terracotta Army Horse", 
    artist: "Unknown", 
    year: "210 BC", 
    period: "Ancient", 
    tags: ["SCULPTURE", "CHINESE"], 
    image_id: "https://images.metmuseum.org/CRDImages/as/original/DP170084.jpg",
    department: "Asian Art",
    culture: "Chinese",
    link: "https://www.metmuseum.org/art/collection/search/39666"
  },
  { 
    id: 15, 
    title: "The Dance Class", 
    artist: "Edgar Degas", 
    year: "1874", 
    period: "Impressionist", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1960.jpg",
    department: "European Paintings",
    culture: "French",
    link: "https://www.metmuseum.org/art/collection/search/438817"
  },
  { 
    id: 16, 
    title: "Autumn Rhythm", 
    artist: "Jackson Pollock", 
    year: "1950", 
    period: "Contemporary", 
    tags: ["PAINTINGS", "AMERICAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ma/original/DT226.jpg",
    department: "Modern Art",
    culture: "American",
    link: "https://www.metmuseum.org/art/collection/search/488978"
  },
  { 
    id: 17, 
    title: "The Crucifixion", 
    artist: "Rogier van der Weyden", 
    year: "1460", 
    period: "Renaissance", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT774.jpg",
    department: "European Paintings",
    culture: "Flemish",
    link: "https://www.metmuseum.org/art/collection/search/435735"
  },
  { 
    id: 18, 
    title: "Bridge over a Pond of Water Lilies", 
    artist: "Claude Monet", 
    year: "1899", 
    period: "Impressionist", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT1037.jpg",
    department: "European Paintings",
    culture: "French",
    link: "https://www.metmuseum.org/art/collection/search/437127"
  },
  { 
    id: 19, 
    title: "The Boating Party", 
    artist: "Mary Cassatt", 
    year: "1893", 
    period: "Impressionist", 
    tags: ["PAINTINGS", "AMERICAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ad/original/DT218.jpg",
    department: "American Wing",
    culture: "American",
    link: "https://www.metmuseum.org/art/collection/search/10943"
  },
  { 
    id: 20, 
    title: "Irises", 
    artist: "Vincent van Gogh", 
    year: "1890", 
    period: "Modern", 
    tags: ["PAINTINGS", "EUROPEAN"], 
    image_id: "https://images.metmuseum.org/CRDImages/ep/original/DT2071.jpg",
    department: "European Paintings",
    culture: "Dutch",
    link: "https://www.metmuseum.org/art/collection/search/436528"
  }
];

/**
 * Default periods for fallback data
 */
export const fallbackPeriods = ['Modern', 'Contemporary', 'Classical'];
